import { useMemo } from 'react';
import { WEBGL_LABEL_NEW_LINE } from '~/_shared/constants/webgl.constants';
import { type LatLngBounds } from '~/_shared/types/latLng';
import { memoizeWeak } from '~/_shared/utils/memoize/memoize';
import { notNullsy } from '~/_shared/utils/typeGuards';
import type { BoundaryGroup } from '~/boundary/boundary.types';
import { type BoundingBox } from '~/map/map/boundingBox';
import { type BoundaryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import { useBoundaryGroupsSelector } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { type BoundaryStateItem } from '~/store/boundaryItems/boundaryItems.state';
import { useMapComponentLastBoundsSelector } from '~/store/frontendState/mapComponent/mapComponent.selectors';
import { type LastBounds } from '~/store/frontendState/mapComponent/mapComponent.state';
import { type BoundaryTextDimensionsCalculator } from '../../mapBoundary.manager';
import { type MapBoundaryDistanceToPixelsMap } from './useMapBoundaryDistanceToPixels';
import { type MapBoundaryLabelContentCallback } from './useMapBoundaryLabelContent';

type BoundaryLabel = Readonly<{
  text: string;
  bounds: LatLngBounds | null;
}>;

export type BoundaryLabelFilterMap = ReadonlyMap<BoundaryGroupId, (boundary: BoundaryStateItem) => BoundaryLabel | null>;

type UseMapBoundaryLabelFilterParams = {
  boundaryGroupIds: ReadonlyArray<number>;
  distanceToPixelsScale?: MapBoundaryDistanceToPixelsMap;
  textDimensionsCalculator?: BoundaryTextDimensionsCalculator;
  useActiveBTGForLabelFiltering?: boolean;
  createLabelContentResolver: (boundaryGroup: BoundaryGroup) => MapBoundaryLabelContentCallback;
};

const emptyFilter = () => null;

export const useMapBoundaryLabelFilter = ({
  boundaryGroupIds,
  distanceToPixelsScale,
  textDimensionsCalculator,
  createLabelContentResolver,
}: UseMapBoundaryLabelFilterParams) => {
  const boundaryGroups = useBoundaryGroupsSelector();
  const lastBounds = useMapComponentLastBoundsSelector();

  return useMemo<BoundaryLabelFilterMap>(() => {
    const result = new Map<BoundaryGroupId, (boundary: BoundaryStateItem) => BoundaryLabel | null>();

    if (!lastBounds || !distanceToPixelsScale || !textDimensionsCalculator) {
      return result;
    }

    for (const boundaryGroupId of boundaryGroupIds) {
      const boundaryGroup = boundaryGroups.find(g => g.id === boundaryGroupId);

      if (!boundaryGroup) {
        result.set(boundaryGroupId, emptyFilter);
        continue;
      }

      const boundaryIsHiddenOnZoomLevel = boundaryGroup.loadZoomLevel > lastBounds.zoomLevel;

      const hideLabelsForCurrentZoomLevel = boundaryIsHiddenOnZoomLevel;

      const getLabelContent = createLabelContentResolver(boundaryGroup);

      const filter = getMapBoundaryLabelFilterMemoized(
        lastBounds,
        hideLabelsForCurrentZoomLevel,
        distanceToPixelsScale,
        textDimensionsCalculator,
        getLabelContent
      );

      result.set(boundaryGroupId, filter);
    }

    return result;
  }, [lastBounds, distanceToPixelsScale, textDimensionsCalculator, boundaryGroupIds, boundaryGroups, createLabelContentResolver]);
};

const getMapBoundaryLabelFilter = (
  lastBounds: LastBounds,
  hideLabelsForCurrentZoomLevel: boolean,
  distanceToPixelsScale: MapBoundaryDistanceToPixelsMap,
  textDimensionsCalculator: BoundaryTextDimensionsCalculator,
  getLabelContent: MapBoundaryLabelContentCallback,
): (boundary: BoundaryStateItem) => BoundaryLabel | null => {
  return (boundary: BoundaryStateItem) => {
    if (!boundary.labelBoundaries || !boundary.displayName) {
      return null;
    }

    if (hideLabelsForCurrentZoomLevel) {
      return null;
    }

    if (isLabelOutOfBounds(lastBounds.bounds, boundary.labelBoundaries)) {
      return null;
    }

    const pixelScale = distanceToPixelsScale[lastBounds.zoomLevel];
    if (!pixelScale) {
      return null;
    }

    const labelContent = getLabelContent(boundary);
    if (!labelContent) {
      return null;
    }

    if (boundary.settings.isArtificial && !labelContent.boundaryValue) {
      return null;
    }

    const nameLine = labelContent.boundaryName;
    const valueLine = labelContent.boundaryValue ?
      `${labelContent.boundaryValueLegend ? `${labelContent.boundaryValueLegend}: ` : ''}${labelContent.boundaryValue}` :
      null;
    const lines = [nameLine, valueLine].filter(notNullsy);
    if (lines.length === 0) {
      return null;
    }

    const boundsHeight = (boundary.labelBoundaries.ne.lat - boundary.labelBoundaries.sw.lat) * pixelScale.latSize;
    const boundsWidth = (boundary.labelBoundaries.ne.lng - boundary.labelBoundaries.sw.lng) * pixelScale.lngSize;

    const textHeight = textDimensionsCalculator.calculateMinHeight();
    const longest = lines.reduce((a, b) => a.length >= b.length ? a : b);
    const textWidth = textDimensionsCalculator.calculateMinWidth(longest);

    if (boundsHeight < textHeight || boundsWidth < textWidth) {
      return null;
    }

    return {
      text: lines.join(WEBGL_LABEL_NEW_LINE),
      bounds: boundary.labelBoundaries,
    };
  };
};
const getMapBoundaryLabelFilterMemoized = memoizeWeak(getMapBoundaryLabelFilter);

const isLabelOutOfBounds = (bounds: BoundingBox, labelBoundaries: LatLngBounds) => !bounds.intersects(labelBoundaries);
